import React from 'react';

import { useB2BRepackagingContext } from '@vimeo/b2b-repackaging';
import { Stack, rem } from '@vimeo/bokeh';

import PlanCard from './plans/PlanCard';
import { PLANS } from '../../utils/constants';
import { defaultTemplateProps, PlanData, UserData } from '../../utils/interface';
import {
  filterPlansData,
  showRecommendedBadge,
  showMonthlyPlans,
  getPreviousPlanTierCopy,
  isSolutionTier,
  isEligibleForFreeTrial,
} from '../../utils/utils';

interface Props {
  modalConfig?: defaultTemplateProps;
  userData: UserData;
  plansData: PlanData[];
  showStorageQuotaModalUpdates?: boolean;
}

const PlanCards = (props: Props) => {
  const MAX_PLANS_TO_DISPLAY = 4;

  // B2B repackaging Segment A: hide individual plans (Creator/Professional) for
  // any enforced, non-whitelisted user — no tier check. The whitelist is the
  // only path out of enforcement, so this stays correct after a business user
  // churns to Free. `showIndividualPlans` already encodes `!enforced || whitelisted`.
  const { showIndividualPlans } = useB2BRepackagingContext();
  const hideIndividualPlans = !showIndividualPlans;

  const excludePlans = hideIndividualPlans
    ? [...(props.modalConfig?.excludePlans ?? []), PLANS.CREATOR, PLANS.PROFESSIONAL]
    : props.modalConfig?.excludePlans;

  const filterdPlans = filterPlansData(props.plansData, props.userData, excludePlans);
  const hideSubheading =
    props.modalConfig?.hidePlansSummary ||
    (filterdPlans.length > 0 && isSolutionTier(filterdPlans[0].tier));

  const isUserEligibleForFreeTrial = isEligibleForFreeTrial(props.userData);
  const allowFreeTrial =
    isUserEligibleForFreeTrial &&
    filterdPlans &&
    Boolean(filterdPlans[0].metadata.interactions.purchase.uri.freeTrial);

  return (
    <Stack
      position="relative"
      zIndex={1}
      padding={`1rem 0 ${rem(20)} 0`}
      direction={{ base: 'column', xl: 'row' }}
      gap="md"
      justifyContent="center"
    >
      {filterdPlans.map((planData: PlanData, index: number) => {
        const previousPlanTierCopy = getPreviousPlanTierCopy(filterdPlans, planData, index);

        const previousPlanTier = index > 0 ? filterdPlans[index - 1].tier : null;
        return (
          index < MAX_PLANS_TO_DISPLAY && (
            <PlanCard
              key={`plan-card-${planData.tier}`}
              planData={planData}
              userData={props.userData}
              previousCardPlan={previousPlanTierCopy}
              modalConfig={props.modalConfig}
              showRecommendedBadge={
                props.modalConfig?.recommendedPlan
                  ? planData.tier === props.modalConfig?.recommendedPlan
                  : showRecommendedBadge(planData, index, previousPlanTier)
              }
              displayMonthlyPlan={showMonthlyPlans(props.plansData)}
              hideSubheading={hideSubheading}
              allowFreeTrial={allowFreeTrial}
              showStorageQuotaModalUpdates={props.showStorageQuotaModalUpdates}
            />
          )
        );
      })}
    </Stack>
  );
};

export default PlanCards;
